import { type HTMLAttributes, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface HeaderProps extends HTMLAttributes<HTMLElement> {
  /**
   * Whether the header is sticky
   * @default false
   */
  sticky?: boolean;

  /**
   * Background variant
   * @default 'default'
   */
  variant?: 'default' | 'transparent' | 'dark';
}

/**
 * Header component
 *
 * Application header with support for logo, navigation, and actions.
 *
 * @example
 * ```tsx
 * <Header sticky>
 *   <HeaderLogo>MedSuite</HeaderLogo>
 *   <HeaderNav>
 *     <a href="/">Home</a>
 *     <a href="/admin">Admin</a>
 *   </HeaderNav>
 *   <HeaderActions>
 *     <Button>Add New</Button>
 *   </HeaderActions>
 * </Header>
 * ```
 */
export const Header = forwardRef<HTMLElement, HeaderProps>(
  ({ sticky = false, variant = 'default', className, children, ...props }, ref) => {
    const baseStyles = [
      'flex items-center justify-between',
      'px-6 py-4',
      'transition-all duration-200',
    ];

    const variantStyles = {
      default: 'bg-white border-b border-gray-200',
      transparent: 'bg-transparent',
      dark: 'bg-gray-900 text-white',
    };

    const stickyStyles = sticky ? 'sticky top-0 z-40 shadow-sm' : '';

    return (
      <header
        ref={ref}
        className={cn(baseStyles, variantStyles[variant], stickyStyles, className)}
        {...props}
      >
        {children}
      </header>
    );
  }
);

Header.displayName = 'Header';

export interface HeaderLogoProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * HeaderLogo component
 *
 * Logo section of the header.
 */
export const HeaderLogo = forwardRef<HTMLDivElement, HeaderLogoProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex items-center gap-3', className)} {...props}>
        {children}
      </div>
    );
  }
);

HeaderLogo.displayName = 'HeaderLogo';

export interface HeaderNavProps extends HTMLAttributes<HTMLElement> {}

/**
 * HeaderNav component
 *
 * Navigation section of the header.
 */
export const HeaderNav = forwardRef<HTMLElement, HeaderNavProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <nav ref={ref} className={cn('flex items-center gap-6 flex-1 mx-8', className)} {...props}>
        {children}
      </nav>
    );
  }
);

HeaderNav.displayName = 'HeaderNav';

export interface HeaderActionsProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * HeaderActions component
 *
 * Actions section of the header (buttons, user menu, etc).
 */
export const HeaderActions = forwardRef<HTMLDivElement, HeaderActionsProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex items-center gap-3', className)} {...props}>
        {children}
      </div>
    );
  }
);

HeaderActions.displayName = 'HeaderActions';
